import { z } from 'zod';
import type { MetadataItem } from '../tool-config-types';

type LoginOutput = {
  username?: string;
  role?: string;
  success?: boolean;
  error?: string;
  message?: string;
};

type MetadataExtractorContext = {
  input: unknown;
  output?: unknown;
  status?: string;
};

const LoginInputSchema = z.object({
  credentialId: z.string(),
});

const isLoginOutput = (output: unknown): output is LoginOutput =>
  typeof output === 'object' && output !== null;

const isRunningStatus = (status?: string): boolean =>
  status === 'started' || status === 'in-progress';

const isErrorStatus = (status?: string): boolean => status === 'error';

export const extractLoginCredentialId = (
  input: unknown,
): string | undefined => {
  const parsedInput = LoginInputSchema.safeParse(input);
  if (!parsedInput.success) {
    return undefined;
  }

  return parsedInput.data.credentialId;
};

function parseLoginString(data: string): LoginOutput {
  const parsed: LoginOutput = { success: true };

  const authenticatedAsMatch = data.match(
    /Authenticated\s+as\s+(\S+)(?:\s*\(([^)]+)\))?/i,
  );
  if (authenticatedAsMatch) {
    parsed.username = authenticatedAsMatch[1].replace(/[.,]$/, '');
    if (authenticatedAsMatch[2]) {
      parsed.role = authenticatedAsMatch[2];
    }
  }

  if (!parsed.username) {
    const usernameMatch = data.match(/username\s+(\S+)/i);
    if (usernameMatch) {
      parsed.username = usernameMatch[1].replace(/[.,]$/, '');
    }
  }

  if (!parsed.role) {
    const roleMatch = data.match(/role\s+(\S+)/i);
    if (roleMatch) {
      parsed.role = roleMatch[1].replace(/[.,]$/, '');
    }
  }

  if (
    data.toLowerCase().includes('error') ||
    data.toLowerCase().includes('failed')
  ) {
    parsed.success = false;
  }

  return parsed;
}

function extractLoginData(output: unknown): LoginOutput | null {
  if (typeof output === 'string') {
    return parseLoginString(output);
  }

  if (isLoginOutput(output)) {
    return {
      username:
        typeof output.username === 'string' ? output.username : undefined,
      role: typeof output.role === 'string' ? output.role : undefined,
      success: output.success !== false && !output.error,
      error: typeof output.error === 'string' ? output.error : undefined,
    };
  }

  return null;
}

export const extractLoginMetadata = ({
  output,
  status,
}: MetadataExtractorContext): MetadataItem[] => {
  if (isRunningStatus(status)) {
    return [];
  }

  const loginData = extractLoginData(output);

  if (
    isErrorStatus(status) ||
    loginData?.error ||
    loginData?.success === false
  ) {
    return [{ label: 'Status', value: 'Failed' }];
  }

  if (loginData?.username) {
    const metadata: MetadataItem[] = [
      { label: 'User', value: loginData.username },
    ];
    if (loginData.role) {
      metadata.push({ label: 'Role', value: loginData.role });
    }
    metadata.push({ label: 'Status', value: '✓', color: 'green' });
    return metadata;
  }

  if (loginData?.success) {
    return [{ label: 'Status', value: '✓', color: 'green' }];
  }

  return [];
};

export const hasLoginExpandableContent = ({
  output,
  status,
}: MetadataExtractorContext): boolean => {
  if (isRunningStatus(status)) return false;
  if (isErrorStatus(status)) return true;

  const loginData = extractLoginData(output);
  return !!(loginData?.error || loginData?.success === false);
};
